import type { ReactNode } from 'react';
import { Icon } from '../chrome/Icon';
import { Sheet } from '../shared/Sheet';
import { MilestoneMemories } from '../media/MilestoneMemories';
import { todayIso } from '../../data/derive';
import { useT } from '../../i18n';
import type { Milestone, MilestoneStatus } from '../../data/types';

interface Props {
  open: boolean;
  milestone: Milestone;
  value: MilestoneStatus;
  onChange: (v: MilestoneStatus) => void;
  dateChoice: string;
  setDateChoice: (v: string) => void;
  /** Selected custom date (ISO 'YYYY-MM-DD') — used when dateChoice === 'custom'. */
  customDate: string;
  setCustomDate: (v: string) => void;
  /** Earliest selectable date for the custom picker (the baby's DOB, ISO). */
  minDate?: string;
  /** Active baby id — enables the encrypted photo uploader on the "Achieved" branch. */
  babyId?: string;
  onSave: () => void;
  onClose: () => void;
  /** Disables the Save button + shows a saving label while the PATCH is in flight. */
  saving?: boolean;
  /** Legacy ignored. */
  accent?: string;
  accentInk?: string;
}

const DATE_OPTIONS = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thisweek',  label: 'This week' },
  { id: 'custom',    label: 'Pick a date' },
];

interface Option {
  id: MilestoneStatus;
  label: string;
  desc: string;
  /** Background colour for the selected ring (CSS value). */
  ringColor: string;
  mark: ReactNode;
}

export function StatusSheet({
  open,
  milestone,
  value,
  onChange,
  dateChoice,
  setDateChoice,
  customDate,
  setCustomDate,
  minDate,
  babyId,
  onSave,
  onClose,
  saving = false,
}: Props) {
  const today = todayIso();
  const t = useT();
  const options: Option[] = [
    {
      id: 'done',
      label: t('status.achieved', 'Achieved'),
      desc: t('status.desc.done', "You've seen this happen — clearly and more than once."),
      ringColor: 'var(--color-accent)',
      mark: (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent">
          <Icon name="check" size={14} color="white" />
        </span>
      ),
    },
    {
      id: 'now',
      label: t('status.inProgress', 'In progress'),
      desc: t('status.desc.now', 'Starting to emerge — some attempts, not yet consistent.'),
      ringColor: 'var(--color-amber)',
      mark: (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber">
          <span className="size-2.5 rounded-full bg-white/85" />
        </span>
      ),
    },
    {
      id: 'soon',
      label: t('status.notStarted', 'Not started'),
      desc: t('status.desc.soon', "Haven't noticed this yet — most likely just early."),
      ringColor: 'var(--color-grey)',
      mark: <span className="size-6 shrink-0 rounded-full border-2 border-grey bg-transparent" />,
    },
  ];

  return (
    <Sheet open={open} onClose={onClose} label={t('status.sheetLabel', 'Update milestone status')}>
      {/* Header */}
      <div className="px-6 pb-1 shrink-0">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
          {t('status.updateTitle', 'Update status')}
        </p>
        <h2 className="mt-1.5 font-serif text-2xl leading-snug tracking-tight text-ink text-pretty">
          {milestone.name}
        </h2>
        <p className="text-[12.5px] text-ink-2 tabular-nums">
          {milestone.cat} · {t('status.usuallyMonths', 'usually {start}–{end} months').replace('{start}', String(milestone.start)).replace('{end}', String(milestone.end))}
        </p>
      </div>

      {/* Options */}
      <div className="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto px-4 pt-5">
        {options.map((opt) => {
          const on = value === opt.id;
          return (
            <div
              key={opt.id}
              role="button"
              tabIndex={0}
              onClick={() => onChange(opt.id)}
              className={[
                'grid grid-cols-[auto_1fr] shrink-0 min-h-11 items-start gap-3.5 rounded-2xl p-4 cursor-pointer transition-colors duration-150',
                on ? 'bg-surface' : 'bg-transparent',
              ].join(' ')}
              style={
                on
                  ? { border: `1.5px solid ${opt.ringColor}` }
                  : { border: '1px solid var(--color-line)' }
              }
            >
              {opt.mark}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-serif text-[17px] tracking-tight text-ink">
                    {opt.label}
                  </span>
                  {on && (
                    <span
                      className="text-[10.5px] font-bold uppercase tracking-wider"
                      style={{ color: opt.ringColor }}
                    >
                      {t('status.selected', 'Selected')}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[13px] leading-snug text-ink-2 text-pretty">
                  {opt.desc}
                </p>

                {on && opt.id === 'done' && (
                  <div className="mt-3.5 space-y-4.5">
                    {/* Date picker */}
                    <div>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                        {t('status.when', 'When did it happen?')}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {DATE_OPTIONS.map((d) => {
                          const dOn = dateChoice === d.id;
                          return (
                            <button
                              key={d.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDateChoice(d.id);
                              }}
                              className={[
                                'inline-flex items-center gap-1.5 rounded-3xl px-3 py-1.5 text-[12.5px] transition-all',
                                dOn ? 'text-white font-semibold border-0' : 'border border-line bg-bg text-ink font-medium',
                              ].join(' ')}
                              style={dOn ? { background: opt.ringColor } : undefined}
                            >
                              {d.id === 'custom' && (
                                <Icon
                                  name="calendar"
                                  size={13}
                                  color={dOn ? 'white' : 'var(--color-ink-2)'}
                                />
                              )}
                              {t('status.when.' + d.id, d.label)}
                            </button>
                          );
                        })}
                      </div>

                      {dateChoice === 'custom' && (
                        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="date"
                            value={customDate || today}
                            min={minDate}
                            max={today}
                            onChange={(e) => setCustomDate(e.target.value)}
                            className="min-h-11 w-full rounded-xl border border-line bg-bg px-3.5 text-[14px] text-ink outline-none focus:border-accent"
                          />
                          <p className="mt-1.5 text-[11px] leading-snug text-ink-3">
                            {t('status.when.customHelp', 'Any day from birth up to today.')}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Memory uploader — real, compressed + (optionally) E2E-encrypted */}
                    {babyId && (
                       <div onClick={(e) => e.stopPropagation()}>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                          {t('status.saveMemory', 'Save a memory · optional')}
                        </p>
                        <MilestoneMemories babyId={babyId} milestoneId={Number(milestone.id)} />
                      </div>
                    )}
                  </div>
                )}

                {on && opt.id === 'now' && (
                  <p className="mt-3 rounded-[10px] bg-amber-soft px-3 py-2.5 text-xs leading-snug text-amber-ink">
                    {t('status.tailoredNudge', "We'll surface tailored daily tasks to nudge this one along.")}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex gap-2.5 px-5 pt-5 pb-7 shrink-0">
        <button
          onClick={onClose}
          className="min-h-13 shrink-0 rounded-3xl border border-line bg-transparent px-5 text-[14.5px] font-semibold text-ink"
        >
          {t('common.cancel', 'Cancel')}
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex min-h-13 flex-1 items-center justify-center gap-2.5 rounded-3xl bg-ink text-[14.5px] font-semibold text-bg disabled:opacity-60"
        >
          <Icon name="check" size={16} color="currentColor" />
          {saving ? t('common.saving', 'Saving…') : t('status.saveStatus', 'Save status')}
        </button>
      </div>
    </Sheet>
  );
}
